'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { installRscCacheObserver } from '@/lib/analytics/rsc-cache-observer'
import { cancelGridMeasurementOnRouteChange } from '@/lib/analytics/grid-navigation-performance'
import { sendWebappLoadPerformance } from '@/lib/analytics/load-performance'

const MAIN_CATEGORIES = new Set(['vfx', 'sfx', 'graphics', '3d'])

export default function WebappPageLoadPerformance() {
  const pathname = usePathname()
  const hasReportedPageLoad = useRef(false)

  useEffect(() => installRscCacheObserver(), [])

  useEffect(() => {
    cancelGridMeasurementOnRouteChange(pathname)
    if (hasReportedPageLoad.current) {
      return
    }

    const reportPageLoad = () => {
      if (hasReportedPageLoad.current) {
        return
      }

      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined

      if (!navigation || navigation.loadEventEnd <= 0) {
        return
      }

      hasReportedPageLoad.current = true
      const mainCategory = new URL(navigation.name).pathname.split('/')[1]

      sendWebappLoadPerformance({
        page_path: new URL(navigation.name).pathname,
        surface: 'pages',
        operation: 'page_load',
        source: 'document',
        ...(MAIN_CATEGORIES.has(mainCategory) ? { main_category: mainCategory } : {}),
        duration_ms: Math.round(navigation.loadEventEnd - navigation.startTime),
        ttfb_ms: Math.round(navigation.responseStart - navigation.startTime),
        response_download_ms: Math.round(navigation.responseEnd - navigation.responseStart),
        ...(performance.getEntriesByName('first-contentful-paint')[0]
          ? { fcp_ms: Math.round(performance.getEntriesByName('first-contentful-paint')[0].startTime) }
          : {}),
        status: 'success',
      })
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    const afterLoad = () => {
      timer = setTimeout(reportPageLoad, 0)
    }
    if (document.readyState === 'complete') afterLoad()
    else window.addEventListener('load', afterLoad, { once: true })

    return () => {
      window.removeEventListener('load', afterLoad)
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [pathname])

  return null
}
